import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { serviceDetailData } from './serviceDetailData';
import WebServicesSection from './WebServicesSection';
import KeyBenefitsSection from './KeyBenefitsSection';
import ServiceNowApproachSection from './ServiceNowApproachSection';
import WhyMobikodeSection from './WhyMobikodeSection';
import LocationForm from '../../Components/mumbai/LocationForm';
import ServiceNowLeadForm from '../../Components/ServiceNowLeadForm';
import ContactSection from '../../Components/home/ContactSection';

function Consulting() {
    const { id } = useParams();
    const navigate = useNavigate();

    // Key lookup or entry find
    const serviceKey = id?.replace(/^\//, '');
    const service =
        serviceDetailData[serviceKey] ||
        Object.entries(serviceDetailData).find(([slug, item]) =>
            slug === serviceKey || item?.link === `/${serviceKey}` || item?.link === serviceKey
        )?.[1];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!service) {
        return (
            <div className="h-screen flex flex-col items-center justify-center bg-white text-[#01386A]">
                <h1 className="text-3xl font-bold mb-4">Service Not Found</h1>
                <button onClick={() => navigate('/')} className="text-[#ED6800] underline font-semibold">
                    Go Back to Home
                </button>
            </div>
        );
    }

    // Dynamic SEO Fields
    const metaTitle = service.seo?.title || `${service.title} | Mobikode`;
    const metaDescription = service.seo?.description || service.subheadline || service.overview;
    const metaKeywords = service.seo?.keywords || "ServiceNow, Mobikode, Enterprise Solutions";
    const currentUrl = `https://mobikode.com/service/${serviceKey}`;

    // Layout Fallbacks
    const heroBadge = service.heroSubTitle || service.badge || `Phase ${service.step}`;
    const heroTitle = service.title;
    const heroDescription = service.description || service.subheadline || service.overview;
    const overviewTitle = service.aTitle || service.tagline || `Transforming Business with ${service.title}`;
    const overviewText = service.aText || service.overview || service.subheadline;


    return (
        <div>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta name="keywords" content={metaKeywords} />
                <link rel="canonical" href={currentUrl} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:type" content="website" />
            </Helmet>
            <section className="relative min-h-[550px] lg:h-[630px] w-full overflow-hidden bg-[#020612] flex items-center">
                {/* Background Image & Gradient */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/services/bg3.png"
                        alt="Hero Background"
                        className="h-full w-full object-cover object-center lg:object-right opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#020612] via-[#020612]/70 to-transparent" />
                </div>

                {/* Main Container */}
                <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px] py-16 lg:py-0 h-full flex flex-col justify-center">

                    {/* Left Side Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        viewport={{ once: true }}
                        className="max-w-[800px] flex flex-col justify-center"
                    >
                        {heroBadge && (
                            <p className="text-white/90 font-graphik text-[14px] sm:text-[16px] font-[500] tracking-wide mb-5">
                                {heroBadge}
                            </p>
                        )}

                        <h1 className="text-[30px] md:text-[55px] font-sora text-white w-full font-[700] leading-[1.15] tracking-tight mb-6">
                            {heroTitle}
                        </h1>

                        {heroDescription && (
                            <p className="text-white font-graphik text-[14px] lg:text-[16px] font-[500] leading-[1.65] max-w-[620px]">
                                {heroDescription}
                            </p>
                        )}
                    </motion.div>

                    {/* Right Floating Card (Absolute bottom-right on Desktop) */}
                    {/* <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                        viewport={{ once: true }}
                        className="mt-8 lg:mt-0 lg:absolute lg:bottom-[50px] lg:right-[80px] z-20 flex justify-start lg:justify-end"
                    >
                        <div className="w-full max-w-[356px] bg-[#FFFFFF0A] backdrop-blur-[20px] border border-[#FFFFFF29] p-6 sm:p-7 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
                            <p className="text-white/90 font-graphik text-[14px] lg:text-[16px] font-[500] leading-[1.65]">
                                Build a secure foundation for data engineering, analytics, and AI with an implementation plan shaped around your business priorities.[cite: 4]
                            </p>
                        </div>
                    </motion.div> */}

                </div>
            </section>

            <section className="bg-white overflow-hidden py-[60px] lg:py-[100px]">
                <div className="max-w-[1440px] w-full mx-auto px-[20px] lg:px-[80px]">

                    {/* Outer Bordered Card */}
                    <div className="w-full bg-white border border-[#D5DDE8] flex flex-col lg:flex-row items-stretch">

                        {/* LEFT IMAGE (Flush to edges of the card) */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="w-full lg:w-full max-w-[498px] overflow-hidden"
                        >
                            <img
                                src="/assets/sales/img1.png"
                                alt="Service Overview"
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                            />
                        </motion.div>

                        {/* RIGHT CONTENT (Padded inside the card) */}
                        <div className="w-full lg:w-full p-6 sm:p-10 lg:p-14 flex flex-col justify-center text-left">

                            {/* Top Orange Badge */}
                            <motion.p
                                initial={{ opacity: 0, y: -10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5 }}
                                className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-3"
                            >
                                Start with the right foundation
                            </motion.p>

                            {/* Title */}
                            <motion.h2
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.15, duration: 0.6 }}
                                className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] leading-[1.25] tracking-tight mb-5"
                            >
                                {overviewTitle}
                            </motion.h2>

                            {/* Description */}
                            <motion.p
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3, duration: 0.6 }}
                                className="font-graphik text-[14px] lg:text-[16px] text-[#49577A] leading-[1.7] font-[500]"
                            >
                                {overviewText}
                            </motion.p>

                        </div>

                    </div>

                </div>
            </section>
            <WebServicesSection service={service} />

            <KeyBenefitsSection service={service} />
            <ServiceNowApproachSection />
            <WhyMobikodeSection />
            <ContactSection />
        </div>
    );
}

export default Consulting;